import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { debounce } from "lodash";
import { getUserType } from "util/auth";
import {
  BrandDetailParams,
  BrandDetailTireEdit,
  BrandDetailTireSelectResponse,
  BrandDetailTireUpdateRequest,
} from "features/proc_fee/types/tire";
import { getSuggestCategory, getTireDetailSelect, updateTireDetail } from "features/proc_fee/api/tireDetailBrandApi";
import { restrictDecimalInput } from "utils";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";

/**
 * Custom hook to manage tire brand details.
 * @returns {object} An object containing the following properties:
 * - {BrandDetailTireEdit[]} tireBrandDetail - The list of tire brand details.
 * - {Function} getBrandDetail - Function to fetch and set tire brand details.
 * - {Function} setTireBrandDetail - Function to manually set tire brand details.
 * - {Function} updateTireBrandDetail - Function to update a specific tire brand detail.
 * @example
 * const { tireBrandDetail, getBrandDetail, setTireBrandDetail, updateTireBrandDetail } = useTireBrandDetail();
 */
const useItemPriceSetting = () => {
  const [categoryOption, setCategoryOption] = useState<{ label: string; value: string }[]>([]);
  const [tireBrandDetail, setTireBrandDetail] = useState<BrandDetailTireEdit[]>([]);
  const [originalTireBrandDetail, setOriginalTireBrandDetail] = useState<BrandDetailTireEdit[]>([]);
  const requestSeqRef = useRef(0);
  const [queryParams, setQueryParams] = useState<BrandDetailParams>({
    limit: DEFAULT_LIMIT_ITEMS,
    offset: 1,
    item_category_m: "",
    maker_or_brand: "",
    // mst_store_price_setting_tire: "0",
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const totalPage = useMemo(() => Math.ceil(total / DEFAULT_LIMIT_ITEMS), [total]);
  const currentPage = useMemo(() => queryParams.offset, [queryParams.offset]);
  const userType = getUserType();
  const [searchValue, setSearchValue] = useState<string>(queryParams.maker_or_brand || "");

  /**
   * Debounced function to update queryParams after user stops typing
   */
  const debouncedUpdateQueryParams = useMemo(
    () =>
      debounce((value: string) => {
        setQueryParams((prev) => {
          if (prev.maker_or_brand !== value) {
            return { ...prev, maker_or_brand: value };
          }
          return prev;
        });
      }, 500),
    [setQueryParams],
  );

  /**
   * Update queryParams when searchValue changes (debounced)
   */
  useEffect(() => {
    debouncedUpdateQueryParams(searchValue);
    return () => {
      debouncedUpdateQueryParams.cancel();
    };
  }, [searchValue, debouncedUpdateQueryParams]);

  /**
   * Sync searchValue with queryParams when queryParams changes externally (e.g., filter reset)
   */
  useEffect(() => {
    setSearchValue(queryParams.maker_or_brand || "");
  }, [queryParams.maker_or_brand]);

  const parseData = (data: BrandDetailTireSelectResponse | undefined) => {
    if (!data) return [];
    return data.list.map((item) => {
      const parsedItem: BrandDetailTireEdit = {
        brand_cd: item.brand_cd,
        maker_name: item.maker_name,
        brand_name: item.brand_name,
        item_category_m: item.item_category_m,
        sale_price_rate_atable: item.sale_price_rate_atable ?? ("" as unknown as number),
        fixed_discount: item.fixed_discount ?? ("" as unknown as number),
        set_profit_ratio: item.set_profit_ratio ?? ("" as unknown as number),
        fixed_discount_color: "black",
        sale_price_rate_atable_color: "black",
        set_profit_ratio_color: "black",
      };
      if (userType === "C0002" || userType === "C0006") {
        parsedItem.fixed_discount_color = !item.fixed_discount_store_login_edit_flag
          ? "black"
          : "red";
        parsedItem.sale_price_rate_atable_color = !item.sale_price_rate_atable_store_login_edit_flag
          ? "black"
          : "red";
        parsedItem.set_profit_ratio_color = !item.set_profit_ratio_store_login_edit_flag
          ? "black"
          : "red";
      }

      return parsedItem;
    });
  };

  const getBrandDetail = useCallback(async (filter = false) => {
    requestSeqRef.current += 1;
    const currentSeq = requestSeqRef.current;

    setLoading(true);

    try {
      const effectiveOffset = filter ? 1 : queryParams.offset;
      const data = await getTireDetailSelect({
        ...queryParams,
        offset: effectiveOffset,
      });

      // Ignore stale responses when user changes filters quickly.
      if (currentSeq !== requestSeqRef.current) return;

      const parsedData = parseData(data);
      setTotal(data?.total || 0);

      // Always replace data for pagination (no longer append).
      setTireBrandDetail(parsedData);
      setOriginalTireBrandDetail(parsedData);
    } finally {
      // Only the latest request can end the loading state.
      if (currentSeq === requestSeqRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, userType]);

  const normalizeValue = (index: number, key: string, value: string | number) => {
    const includeNumberParseKey = ["fixed_discount", "set_profit_ratio"];
    if (includeNumberParseKey.includes(key)) {
      if (typeof value === "string" && /^0\d+$/.test(value)) {
        return parseInt(value, 10);
      }
      const finalValue = value === "" ? 0 : +value;
      setTireBrandDetail((prev) => {
        const newTireBrandDetail = [...prev];
        newTireBrandDetail[index] = {
          ...newTireBrandDetail[index],
          [key]: finalValue,
        };
        return newTireBrandDetail;
      });
      return value === "" ? 0 : +value;
    }

    if (key === "sale_price_rate_atable" && typeof value === "string") {
      return restrictDecimalInput(value, 3, true);
    }
    return value;
  };

  const updateTireBrandDetail = async (index: number, key: keyof BrandDetailTireUpdateRequest) => {
    const data = tireBrandDetail[index];
    const originalData = originalTireBrandDetail[index];
    if (!data) return;
    if (!originalData) return;

    const numericKeys = ["sale_price_rate_atable", "fixed_discount", "set_profit_ratio"];
    const isCleared =
      numericKeys.includes(key) &&
      data[key] === "" &&
      originalData[key] !== "" &&
      originalData[key] !== null &&
      originalData[key] !== undefined;

    if (data[key] === undefined || originalData[key] === undefined) return;

    // Decide no-op BEFORE calling normalizeValue, because normalizeValue has a side effect
    // of writing 0 back into state for fixed_discount / set_profit_ratio when the value is
    // empty. Without this guard, blurring an originally-empty cell with no input would flip
    // the display from "" to "0".
    if (!isCleared) {
      const originalIsEmpty =
        originalData[key] === "" ||
        originalData[key] === null ||
        originalData[key] === undefined;
      if (originalIsEmpty) {
        if (data[key] === "" || data[key] === null || data[key] === undefined) return;
      } else if (
        data[key] === originalData[key] ||
        Number(data[key]) === Number(originalData[key])
      ) {
        return;
      }
    }

    const value = normalizeValue(index, key, data[key] as string | number);
    if (isCleared) {
      setTireBrandDetail((prev) => {
        const newTireBrandDetail = [...prev];
        newTireBrandDetail[index] = {
          ...newTireBrandDetail[index],
          [key]: "" as unknown as number,
        };
        return newTireBrandDetail;
      });
    }

    const finalValue = isCleared ? 0 : value;
    const updateData: BrandDetailTireUpdateRequest = {
      brand_cd: data.brand_cd,
      // store_cd: localStorage.getItem("shop_cd") || "",
      // mst_store_price_setting_tire: data.mst_store_price_setting_tire || "0",
      [key]: finalValue,
      ...(isCleared && { column_delete_flag: 1 }),
    };
    await updateTireDetail(updateData);
    getBrandDetail(true);
    setOriginalTireBrandDetail((prev) => {
      const newOriginalTireBrandDetail = [...prev];
      newOriginalTireBrandDetail[index] = {
        ...newOriginalTireBrandDetail[index],
        [key]: isCleared ? ("" as unknown as number) : finalValue,
      };
      return newOriginalTireBrandDetail;
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setQueryParams((prev) => ({
      ...prev,
      offset: page,
    }));
  };

  // Track previous filter values to detect changes
  const prevFiltersRef = useRef<{ item_category_m: string; maker_or_brand: string; offset: number } | null>(null);

  useEffect(() => {
    const getCategoryOption = async () => {
      const { list } = await getSuggestCategory();
      if (list && list.length > 0) {
        setCategoryOption([
          ...list.map((item) => ({ label: item.item_category_m, value: item.item_category_m })),
        ]);
      } else {
        setCategoryOption([]);
      }
    };

    getCategoryOption();
  }, []);

  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const isInitialMount = prevFilters === null;

    const filterChanged =
      !isInitialMount &&
      (prevFilters.item_category_m !== queryParams.item_category_m ||
        prevFilters.maker_or_brand !== queryParams.maker_or_brand);

    // Update ref with current values
    prevFiltersRef.current = {
      item_category_m: queryParams.item_category_m || "",
      maker_or_brand: queryParams.maker_or_brand || "",
      offset: queryParams.offset,
    };

    // When filter changes (not initial mount), reset pagination to first page
    if (filterChanged && queryParams.offset !== 1) {
      setQueryParams((prev) => ({ ...prev, offset: 1 }));
      return; // The offset change will trigger this effect again
    }

    // Call API: on initial mount or when filters/offset change
    getBrandDetail(filterChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.offset, queryParams.item_category_m, queryParams.maker_or_brand]);

  return {
    categoryOption,
    tireBrandDetail,
    getBrandDetail,
    setTireBrandDetail,
    updateTireBrandDetail,
    handlePageChange,
    loading,
    setQueryParams,
    queryParams,
    totalPage,
    currentPage,
    searchValue,
    setSearchValue,
  };
};

export default useItemPriceSetting;
